import type { FC, HTMLAttributes, ReactNode } from 'react';

import { TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';

import { cn } from '@shared/utils';

interface TabItem {
  label: ReactNode;
  value: string;
  content: ReactNode;
  contentTestId: string;
}

interface GenericTabsProps extends HTMLAttributes<HTMLDivElement> {
  tabs: TabItem[];
  variant?: 'page' | 'container';
}

export const GenericTabList: FC<GenericTabsProps> = ({ tabs, className, variant = 'container', ...props }) => {
  const isPageLevel = variant === 'page';
  return (
    <TabsList
      {...props}
      className={cn('w-auto justify-start rounded-none bg-transparent p-0 flex gap-6 -translate-y-px', className)}
    >
      {tabs.map((tab) => (
        <TabsTrigger
          key={tab.value}
          value={tab.value}
          className={cn(
            'relative h-8 pb-3 rounded-none border-b-2 border-b-transparent bg-transparent shadow-none transition-all hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none translate-y-px cursor-pointer',
            !isPageLevel && 'text-[13px] font-semibold text-muted-foreground',
            isPageLevel && 'text-base font-normal text-muted-foreground',
          )}
        >
          {tab.label}
        </TabsTrigger>
      ))}
    </TabsList>
  );
};

export const GenericTabContent = ({ tabs }: { tabs: TabItem[] }) => {
  return (
    <>
      {tabs.map((tab) => (
        <TabsContent
          key={tab.value}
          value={tab.value}
          className="flex flex-1 min-h-0 flex-col data-[state=inactive]:hidden"
          data-testid={tab.contentTestId}
        >
          {tab.content}
        </TabsContent>
      ))}
    </>
  );
};
